import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  tasksAtom,
  fetchTasksAtom,
  updateTaskAtom
} from '../../atoms/v0/taskAtoms';
import { fetchProjectsAtom, projectsAtom } from '../../atoms/v0/projectAtoms';
import { FocusHeader } from './components/focus/FocusHeader';
import { FocusTaskItem } from './components/focus/FocusTaskItem';
import { FocusProgressStats } from './components/focus/FocusProgressStats';
import { TaskDetailSheet } from './components/tasks/TaskDetailSheet';
import { FocusNavigationBar } from './components/focus/FocusNavigationBar';
import { TaskList } from './components/tasks/TaskList';
import { groupTasksByTime } from '../../utils/taskUtils';
import {
  playingTaskIdsAtom,
  syncFocusStateWithTasksAtom,
  cleanupExpiredFocusStateAtom,
  syncUserTrackingStateAtom,
  stopTaskTrackingAtom,
} from '../../atoms/v3/focusAtoms';
import { trackEvent } from '../../lib/analytics';
import { trackFocusEvents, trackTaskEvents } from '../../lib/analyticsEnhanced';

/**
 * 专注页面
 * 显示今天的任务，并提供专注模式功能
 */
export const Focus: React.FC = () => {
  const { t } = useTranslation(['v3/common', 'v3/focus']);
  const navigate = useNavigate();

  // 容器引用
  const containerRef = useRef<HTMLDivElement>(null);

  // 状态
  const tasks = useAtomValue(tasksAtom);
  const projects = useAtomValue(projectsAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const playingTaskIds = useAtomValue(playingTaskIdsAtom);
  const [, syncFocusState] = useAtom(syncFocusStateWithTasksAtom);
  const [, cleanupExpiredState] = useAtom(cleanupExpiredFocusStateAtom);
  const [, syncUserTrackingState] = useAtom(syncUserTrackingStateAtom);
  const [, stopTaskTracking] = useAtom(stopTaskTrackingAtom);

  // 组折叠状态
  const [inProgressCollapsed, setInProgressCollapsed] = useState(false);
  const [todayCollapsed, setTodayCollapsed] = useState(false);

  // 合成带有项目名的任务列表 - 使用 useMemo 确保同步更新
  const tasksWithProjects = useMemo(() => {
    // 创建项目映射表
    const projectsMap = projects.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {} as Record<string, { id: string; title: string }>);

    return tasks.map(task => {
      if (task.projectId && projectsMap[task.projectId]) {
        return {
          ...task,
          project: {
            id: projectsMap[task.projectId].id,
            title: projectsMap[task.projectId].title
          }
        };
      }
      return task;
    });
  }, [tasks, projects]);

  // 初始化数据
  useEffect(() => {
    fetchTasks();
    fetchProjects();
    // 同步云端追踪状态
    syncUserTrackingState();
  }, [fetchTasks, fetchProjects, syncUserTrackingState]);

  // 同步 focus 状态 - 当任务列表变化时清理无效状态
  useEffect(() => {
    syncFocusState();
  }, [tasks, syncFocusState]);

  // 组件挂载时清理过期状态
  useEffect(() => {
    cleanupExpiredState();
  }, [cleanupExpiredState]);

  // 按时间分组任务
  const tasksByTime = groupTasksByTime(tasksWithProjects);

  // 获取今天的未完成任务
  const todayTasks = (tasksByTime['today'] || []).filter(task => !task.done);

  // 处理任务点击
  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
    setIsTaskDetailOpen(true);
  };

  // 处理项目点击 - 导航到项目详情页
  const handleProjectClick = (projectId: string) => {
    navigate(`/v3/projects/${projectId}`);
  };

  // 处理任务状态切换
  const handleToggleTask = async (taskId: string, completed: boolean) => {
    try {
      const taskToUpdate = tasksWithProjects.find(t => t.id === taskId);
      const success = await updateTask({
        id: taskId,
        done: completed,
      });

      if (success) {
        // 记录任务状态切换事件（原有）
        trackEvent(completed ? 'task completed' : 'task uncompleted', {
          task_id: taskId,
          task_title: taskToUpdate?.title.substring(0, 50) || '',
          from_focus_page: true
        });

        // 记录增强的任务状态切换事件
        trackTaskEvents.taskToggled(taskId, completed, {
          title: taskToUpdate?.title || '',
          projectId: taskToUpdate?.projectId,
          dueAt: taskToUpdate?.dueAt,
          vital: taskToUpdate?.vital,
          urgent: taskToUpdate?.urgent,
          from_focus_page: true,
          is_playing: playingTaskIds.includes(taskId)
        });

        // 如果任务完成且正在追踪中，停止追踪
        if (completed && playingTaskIds.includes(taskId)) {
          stopTaskTracking(taskId);
        }

        // 如果是循环任务完成，延迟刷新任务列表以显示新生成的任务
        if (completed && taskToUpdate?.recurrenceRule && !taskToUpdate.done) {
          console.log('循环任务完成，将刷新任务列表以显示新生成的任务', { taskId, recurrenceRule: taskToUpdate.recurrenceRule });

          // 延迟1.5秒刷新，给后端时间生成新任务
          setTimeout(() => {
            fetchTasks();
            console.log('循环任务完成后刷新任务列表');
          }, 1500);
        }
      }
    } catch (error) {
      console.error(t('v3/focus:errors.toggleTaskFailed'), error);
    }
  };

  // 处理任务详情关闭
  const handleTaskDetailClose = () => {
    setIsTaskDetailOpen(false);
    // 刷新任务列表
    fetchTasks();
  };

  return (
    <div className="bg-ios-gray6 dark:bg-ios-dark-gray6">
      {/* 导航栏 */}
      <FocusNavigationBar
        title={t('v3/focus:title')}
        largeTitleMode={true}
      />

      {/* 可滚动内容区域 */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom))] overscroll-bounce"
        style={{ overscrollBehavior: 'contain' }}
      >
        {/* 专注头部 - 时钟或任务卡片 */}
        <FocusHeader />

        {/* 分隔线 - 半透明效果 */}
        <div className="mx-4 h-px bg-ios-gray4/30 dark:bg-ios-dark-gray4/30 my-2"></div>

        {/* 任务列表区域 */}
        <div className="px-4 mt-2">
          {/* 标题区域 - 使用更精致的 iOS 风格 */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-ios-title3 font-semibold text-ios-black dark:text-white flex items-center">
              <span className="text-ios-blue dark:text-ios-dark-blue mr-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              {t('v3/focus:nextToDo')}
            </h2>
          </div>

          {/* 正在进行的任务列表 */}
          {playingTaskIds.length > 0 && (() => {
            const currentPlayingTasks = playingTaskIds
              .map(id => tasksWithProjects.find(t => t.id === id && !t.done))
              .filter((task): task is NonNullable<typeof task> => !!task);

            return (
              <TaskList
                tasks={currentPlayingTasks}
                title={t('v3/focus:inProgress')}
                icon={<div className="w-2 h-2 rounded-full bg-ios-red dark:bg-ios-dark-red flex items-center justify-center"></div>}
                color="text-ios-red dark:text-ios-dark-red"
                onToggleTask={handleToggleTask}
                onTaskClick={handleTaskClick}
                isCollapsed={inProgressCollapsed}
                onTitleClick={() => setInProgressCollapsed(!inProgressCollapsed)}
                hideProjectName={false}
                showAddButton={true}
                noMargin={true} // 移除外边距，避免多余的左右边距
                disableTitleClick={true} // 禁用标题点击，只允许通过折叠箭头控制折叠状态
                isTimeGroup={true} // 设置为时间组，使快速添加时显示项目建议而非时间建议
                timeGroupDate={new Date()} // 设置默认日期为当前时间
                customTaskRenderer={(task, _, isFirst, isLast) => (
                  <FocusTaskItem
                    key={`playing-${task.id}`}
                    task={task}
                    onToggle={handleToggleTask}
                    onClick={handleTaskClick}
                    onProjectClick={handleProjectClick}
                    isFirst={isFirst}
                    isLast={isLast}
                    hideProjectName={false}
                  />
                )}
              />
            );
          })()}

          {/* 今天的任务列表 */}
          <TaskList
            tasks={todayTasks.filter(task => !playingTaskIds.includes(task.id))}
            title={t('v3/focus:today')}
            icon={<div className="w-2 h-2 rounded-full bg-ios-blue dark:bg-ios-dark-blue flex items-center justify-center"></div>}
            color="text-ios-blue dark:text-ios-dark-blue"
            onToggleTask={handleToggleTask}
            onTaskClick={handleTaskClick}
            isCollapsed={todayCollapsed}
            onTitleClick={() => setTodayCollapsed(!todayCollapsed)}
            hideProjectName={false}
            showAddButton={true}
            noMargin={true} // 移除外边距，避免多余的左右边距
            disableTitleClick={true} // 禁用标题点击，只允许通过折叠箭头控制折叠状态
            isTimeGroup={true} // 设置为时间组，使快速添加时显示项目建议而非时间建议
            timeGroupDate={new Date()} // 设置默认日期为今天
            customTaskRenderer={(task, _, isFirst, isLast) => (
              <FocusTaskItem
                key={task.id}
                task={task}
                onToggle={handleToggleTask}
                onClick={handleTaskClick}
                onProjectClick={handleProjectClick}
                isFirst={isFirst}
                isLast={isLast}
                hideProjectName={false}
              />
            )}
          />

          {/* 提示信息 - iOS 风格的提示卡片 */}
          <div className="bg-ios-blue/5 dark:bg-ios-dark-blue/10 rounded-ios-lg p-4 mb-6 mt-6 border border-ios-blue/10 dark:border-ios-dark-blue/20">
            <div className="flex">
              <div className="text-ios-blue dark:text-ios-dark-blue mr-3">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4 className="text-ios-subheadline font-medium text-ios-black dark:text-white mb-1">
                  {t('v3/focus:tips.title')}
                </h4>
                <p className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  {t('v3/focus:tips.description')}
                </p>
              </div>
            </div>
          </div>

          {/* 进度统计与可视化 */}
          <FocusProgressStats />
        </div>
      </div>

      {/* 任务详情浮层 */}
      <TaskDetailSheet
        isOpen={isTaskDetailOpen}
        taskId={selectedTaskId}
        onClose={handleTaskDetailClose}
      />
    </div>
  );
};
